/**
 * Minimal OKLCH <-> sRGB colour math.
 *
 * Exists so tests can verify the brand-derived accent tokens in
 * `src/index.css` (`oklch(from var(--brand) L min(c * mult, cap) h)`,
 * design system spec §2) without a browser. `oklchToHex` matches the way
 * Chromium actually resolves an out-of-gamut `oklch()` value for
 * `background`/`color`: plain per-channel clipping of linear sRGB, NOT the
 * CSS Color 4 §13.2 chroma-reduction ("MINDE") gamut-mapping algorithm —
 * the two disagree substantially for a target this far out of gamut (e.g.
 * `oklch(0.88 0.22 263)`), and this module is pinned to whichever one a
 * real render produces. See `__tests__/oklch.test.js`, which reproduces the
 * exact hex values the design spec's own derivation table
 * (`.superpowers/sdd/2026-09-19-ui-revamp/visual-direction.md` §2) lists for
 * the current default brand and a hypothetical red.
 */

function srgbToLinear(channel) {
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
}

function linearToSrgb(channel) {
  return channel <= 0.0031308 ? channel * 12.92 : 1.055 * channel ** (1 / 2.4) - 0.055
}

/**
 * @param {string} hex - `#rrggbb`
 * @returns {[number, number, number]} linear sRGB, each 0..1
 */
function hexToLinearRgb(hex) {
  const normalized = hex.replace('#', '')
  return [0, 2, 4].map((offset) =>
    srgbToLinear(parseInt(normalized.slice(offset, offset + 2), 16) / 255)
  )
}

/**
 * Björn Ottosson's OKLab matrices (https://bottosson.github.io/posts/oklab/).
 *
 * @param {[number, number, number]} rgb - linear sRGB, 0..1
 * @returns {[number, number, number]} OKLab [L, a, b]
 */
function linearRgbToOklab([r, g, b]) {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b
  const l_ = Math.cbrt(l)
  const m_ = Math.cbrt(m)
  const s_ = Math.cbrt(s)
  return [
    0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  ]
}

/**
 * @param {[number, number, number]} lab - OKLab [L, a, b]
 * @returns {[number, number, number]} linear sRGB, unclipped (may fall outside 0..1)
 */
function oklabToLinearRgb([L, a, b]) {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b
  const s_ = L - 0.0894841775 * a - 1.291485548 * b
  const l = l_ ** 3
  const m = m_ ** 3
  const s = s_ ** 3
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ]
}

/**
 * @param {string} hex - `#rrggbb`
 * @returns {{L: number, C: number, H: number}} H in degrees, 0..360
 */
export function hexToOklch(hex) {
  const [L, a, b] = linearRgbToOklab(hexToLinearRgb(hex))
  const C = Math.hypot(a, b)
  let H = (Math.atan2(b, a) * 180) / Math.PI
  if (H < 0) H += 360
  return { L, C, H }
}

/**
 * `oklch(L C H)` resolved to a hex colour, clipped to the sRGB gamut the way
 * a browser clips it for a plain `background`/`color` value (see module
 * comment).
 *
 * @param {number} L
 * @param {number} C
 * @param {number} H - degrees
 * @returns {string} `#rrggbb`
 */
export function oklchToHex(L, C, H) {
  const Hrad = (H * Math.PI) / 180
  const clippedLinear = oklabToLinearRgb([L, C * Math.cos(Hrad), C * Math.sin(Hrad)]).map(
    (channel) => Math.min(1, Math.max(0, channel))
  )
  const srgb = clippedLinear.map(linearToSrgb)
  return (
    '#' +
    srgb.map((channel) => Math.round(channel * 255).toString(16).padStart(2, '0')).join('')
  )
}

/**
 * `oklch(from var(--brand) L min(c * chromaMult, chromaCap) h)` — the recipe
 * every accent token in `index.css` follows: fixed target lightness, the
 * brand's own chroma scaled and capped, the brand's own hue.
 *
 * @param {string} brandHex
 * @param {number} L
 * @param {number} chromaMult
 * @param {number} chromaCap
 * @returns {string} `#rrggbb`
 */
export function fromBrand(brandHex, L, chromaMult, chromaCap) {
  const { C, H } = hexToOklch(brandHex)
  return oklchToHex(L, Math.min(C * chromaMult, chromaCap), H)
}

/**
 * WCAG relative luminance (https://www.w3.org/TR/WCAG21/#dfn-relative-luminance).
 *
 * @param {string} hex - `#rrggbb`
 * @returns {number}
 */
function relativeLuminance(hex) {
  const [r, g, b] = hexToLinearRgb(hex)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/**
 * WCAG contrast ratio (https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio).
 *
 * @param {string} hexA
 * @param {string} hexB
 * @returns {number}
 */
export function contrastRatio(hexA, hexB) {
  const a = relativeLuminance(hexA)
  const b = relativeLuminance(hexB)
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
}
